import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  LuImages, LuImageOff, LuLoader, LuRefreshCw, LuX,
  LuChevronLeft, LuChevronRight, LuExternalLink, LuMaximize2, LuMinimize2
} from 'react-icons/lu';
import { useCrawlerScreenshots } from '../../../../hooks/API/wizard.api';
import { truncateWords } from '../../../../utils/truncateWords';
import '../../../../styles/wizard.css';

const DEFAULT_PAGE_SIZE = 4; // three rows of four cards on desktop
const MAX_PATH_WORDS = 10;
const SKELETON_COUNT = 8;
const API_BASE_URL = String(process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');

const formatCount = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '0';
  return num.toLocaleString('en-US');
};

// Screenshot URLs are expected to be absolute; relative paths are resolved against the API host.
const resolveScreenshotUrl = (url) => {
  if (typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^(https?:|data:|blob:)/i.test(trimmed) || trimmed.startsWith('//')) return trimmed;
  return `${API_BASE_URL}/${trimmed.replace(/^\/+/, '')}`;
};

const getScreenshotPath = (screenshot) => {
  const value = screenshot?.url_path ?? screenshot?.page_url ?? screenshot?.url ?? '';
  return String(value).trim() || 'Untitled page';
};

const getScreenshotKey = (screenshot) =>
  screenshot?.screenshot_id ?? screenshot?.id ?? screenshot?.screenshot_url ?? null;

// The wizard scrolls inside `.wizard-main`, not the window, so the observer needs the real scroll root.
const getScrollParent = (node) => {
  let element = node?.parentElement;
  while (element && element !== document.body) {
    const { overflowY } = window.getComputedStyle(element);
    if (overflowY === 'auto' || overflowY === 'scroll') return element;
    element = element.parentElement;
  }
  return null;
};

const supportsIntersectionObserver = typeof window !== 'undefined' && 'IntersectionObserver' in window;

const ScreenshotCard = React.memo(function ScreenshotCard({ screenshot, index, onOpen }) {
  const tooltipId = useId();
  const src = resolveScreenshotUrl(screenshot?.screenshot_url);
  const fullPath = getScreenshotPath(screenshot);
  const { text: displayPath, isTruncated } = useMemo(
    () => truncateWords(fullPath, MAX_PATH_WORDS),
    [fullPath]
  );

  // 'loading' | 'loaded' | 'error'
  const [imageState, setImageState] = useState(src ? 'loading' : 'error');
  const [isOverflowing, setIsOverflowing] = useState(false);
  const pathTextRef = useRef(null);

  useEffect(() => {
    setImageState(src ? 'loading' : 'error');
  }, [src]);

  // Long single-token paths are clipped by CSS; detect that so the tooltip covers those too.
  useLayoutEffect(() => {
    const element = pathTextRef.current;
    if (!element) return undefined;

    const measure = () => setIsOverflowing(element.scrollWidth > element.clientWidth + 1);
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [displayPath]);

  const showTooltip = isTruncated || isOverflowing;

  return (
    <div className="discovery-screenshot-card" role="listitem">
      <button
        type="button"
        className="discovery-screenshot-thumb"
        onClick={() => onOpen(index)}
        aria-label={`Preview screenshot of ${fullPath}`}
      >
        {imageState === 'loading' && (
          <span className="discovery-screenshot-skeleton-block" aria-hidden="true" />
        )}
        {imageState === 'error' ? (
          <span className="discovery-screenshot-fallback">
            <LuImageOff size={26} />
            <span>Preview unavailable</span>
          </span>
        ) : (
          <img
            src={src}
            alt={`Screenshot of ${fullPath}`}
            className={`discovery-screenshot-img${imageState === 'loaded' ? ' is-loaded' : ''}`}
            loading="lazy"
            decoding="async"
            draggable={false}
            onLoad={() => setImageState('loaded')}
            onError={() => setImageState('error')}
          />
        )}
        <span className="discovery-screenshot-zoom" aria-hidden="true">
          <LuMaximize2 size={14} />
          Preview
        </span>
      </button>

      <div
        className={`discovery-screenshot-path${showTooltip ? ' has-tooltip' : ''}`}
        tabIndex={showTooltip ? 0 : undefined}
        aria-describedby={showTooltip ? tooltipId : undefined}
      >
        <span ref={pathTextRef} className="discovery-screenshot-path-text">{displayPath}</span>
        {showTooltip && (
          <span id={tooltipId} role="tooltip" className="discovery-screenshot-tooltip">
            {fullPath}
          </span>
        )}
      </div>
    </div>
  );
});

const ScreenshotPreviewModal = ({
  items, index, onClose, onNavigate, canLoadMore, isLoadingMore, onLoadMore
}) => {
  const item = items[index];
  const src = resolveScreenshotUrl(item?.screenshot?.screenshot_url);
  const fullPath = getScreenshotPath(item?.screenshot);
  const [imageState, setImageState] = useState(src ? 'loading' : 'error');
  // 'fit' shows the whole image on screen; 'width' fills the width and scrolls (readable full-page captures).
  const [viewMode, setViewMode] = useState('fit');
  const closeButtonRef = useRef(null);

  const hasPrevious = index > 0;
  const toggleViewMode = () => setViewMode((mode) => (mode === 'fit' ? 'width' : 'fit'));
  const hasNext = index < items.length - 1;

  useEffect(() => {
    setImageState(src ? 'loading' : 'error');
  }, [src]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowLeft') {
        onNavigate(-1);
      } else if (event.key === 'ArrowRight') {
        onNavigate(1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNavigate]);

  const stop = (event) => event.stopPropagation();

  return ReactDOM.createPortal(
    <div
      className="discovery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Screenshot preview"
      onClick={onClose}
    >
      <div className="discovery-lightbox-topbar" onClick={stop}>
        <div className="discovery-lightbox-title">
          <LuImages size={18} />
          <span className="discovery-lightbox-path" title={fullPath}>{fullPath}</span>
          <span className="discovery-lightbox-counter">
            {index + 1} / {items.length}{canLoadMore ? '+' : ''}
          </span>
        </div>
        <div className="discovery-lightbox-actions">
          {imageState === 'loaded' && (
            <button
              type="button"
              className="discovery-lightbox-icon-btn is-labelled"
              onClick={toggleViewMode}
              aria-pressed={viewMode === 'width'}
              title={viewMode === 'fit' ? 'Show at full width (scroll to read)' : 'Fit whole image on screen'}
            >
              {viewMode === 'fit' ? <LuMaximize2 size={16} /> : <LuMinimize2 size={16} />}
              <span>{viewMode === 'fit' ? 'Full width' : 'Fit to screen'}</span>
            </button>
          )}
          {src && (
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="discovery-lightbox-icon-btn"
              aria-label="Open image in a new tab"
              title="Open in new tab"
            >
              <LuExternalLink size={18} />
            </a>
          )}
          <button
            ref={closeButtonRef}
            type="button"
            className="discovery-lightbox-icon-btn"
            onClick={onClose}
            aria-label="Close preview"
            title="Close (Esc)"
          >
            <LuX size={20} />
          </button>
        </div>
      </div>

      <div className="discovery-lightbox-body">
        <button
          type="button"
          className="discovery-lightbox-nav prev"
          onClick={(event) => { stop(event); onNavigate(-1); }}
          disabled={!hasPrevious}
          aria-label="Previous screenshot"
          title="Previous (←)"
        >
          <LuChevronLeft size={28} />
        </button>

        <figure className={`discovery-lightbox-figure is-${viewMode}`} onClick={stop}>
          {imageState === 'loading' && (
            <div className="discovery-lightbox-loading">
              <LuLoader size={32} className="spinner" />
            </div>
          )}
          {imageState === 'error' ? (
            <div className="discovery-lightbox-error">
              <LuImageOff size={40} />
              <strong>This screenshot could not be loaded</strong>
              <span>The image may have been moved or is temporarily unavailable.</span>
            </div>
          ) : (
            <img
              key={src}
              src={src}
              alt={`Screenshot of ${fullPath}`}
              className={`discovery-lightbox-img${imageState === 'loaded' ? ' is-loaded' : ''}`}
              draggable={false}
              onClick={toggleViewMode}
              title={viewMode === 'fit' ? 'Click to view at full width' : 'Click to fit on screen'}
              onLoad={() => setImageState('loaded')}
              onError={() => setImageState('error')}
            />
          )}
        </figure>

        <button
          type="button"
          className="discovery-lightbox-nav next"
          onClick={(event) => {
            stop(event);
            if (hasNext) onNavigate(1);
            else onLoadMore();
          }}
          disabled={(!hasNext && !canLoadMore) || (!hasNext && isLoadingMore)}
          aria-label={hasNext ? 'Next screenshot' : 'Load more screenshots'}
          title={hasNext ? 'Next (→)' : 'Load more'}
        >
          {!hasNext && isLoadingMore ? <LuLoader size={24} className="spinner" /> : <LuChevronRight size={28} />}
        </button>
      </div>

      <div className="discovery-lightbox-hint" aria-hidden="true">
        Esc to close &middot; &larr; &rarr; to navigate &middot; Click outside the image to close
      </div>
    </div>,
    document.body
  );
};

const DiscoveryScreenshotGallery = ({ projectId, totalScreenshots, pageSize = DEFAULT_PAGE_SIZE }) => {
  const headingId = useId();
  const {
    data, isLoading, isError, refetch,
    fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError
  } = useCrawlerScreenshots(projectId, { pageSize });

  // Flatten pages and drop duplicates so re-fetched or overlapping pages never render twice.
  const items = useMemo(() => {
    const seen = new Set();
    const list = [];
    (data?.pages ?? []).forEach((page) => {
      (page?.screenshots ?? []).forEach((screenshot) => {
        const key = getScreenshotKey(screenshot) ?? `screenshot-${list.length}`;
        if (seen.has(key)) return;
        seen.add(key);
        list.push({ key, screenshot });
      });
    });
    return list;
  }, [data]);

  const lastPage = data?.pages?.[data.pages.length - 1];
  const knownTotal = lastPage?.totalItems ?? (Number.isFinite(Number(totalScreenshots)) ? Number(totalScreenshots) : null);
  const displayTotal = !hasNextPage ? items.length : (knownTotal ?? items.length);

  const [previewIndex, setPreviewIndex] = useState(null);
  const loadMoreRef = useRef(null);
  const isFetchingRef = useRef(false);

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingRef.current) return;
    isFetchingRef.current = true;
    fetchNextPage({ cancelRefetch: false }).finally(() => {
      isFetchingRef.current = false;
    });
  }, [hasNextPage, fetchNextPage]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !supportsIntersectionObserver) return undefined;
    if (isLoading || !hasNextPage || isFetchingNextPage || isFetchNextPageError) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { root: getScrollParent(sentinel), rootMargin: '0px 0px 320px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isLoading, hasNextPage, isFetchingNextPage, isFetchNextPageError, loadMore, items.length]);

  useEffect(() => {
    if (previewIndex !== null && previewIndex >= items.length) {
      setPreviewIndex(items.length > 0 ? items.length - 1 : null);
    }
  }, [previewIndex, items.length]);

  const openPreview = useCallback((index) => setPreviewIndex(index), []);
  const closePreview = useCallback(() => setPreviewIndex(null), []);
  const navigatePreview = useCallback((delta) => {
    setPreviewIndex((current) => {
      if (current === null) return current;
      const next = current + delta;
      if (next < 0 || next >= items.length) return current;
      return next;
    });
  }, [items.length]);

  const hasInitialError = isError && items.length === 0;

  const renderPanelContent = () => {
    if (isLoading) {
      return (
        <div className="discovery-screenshot-grid" aria-busy="true" aria-label="Loading screenshots">
          {Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <div key={i} className="discovery-screenshot-card is-skeleton" aria-hidden="true">
              <div className="discovery-screenshot-thumb">
                <span className="discovery-screenshot-skeleton-block" />
              </div>
              <div className="discovery-screenshot-path">
                <span className="discovery-screenshot-skeleton-line" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (hasInitialError) {
      return (
        <div className="discovery-screenshot-state is-error" role="alert">
          <LuImageOff size={32} />
          <strong>We couldn&apos;t load the screenshots</strong>
          <span>Something went wrong while fetching the crawled screenshots. Please try again.</span>
          <button type="button" className="wizard-btn wizard-btn-secondary" onClick={() => refetch()}>
            <LuRefreshCw size={16} />
            Retry
          </button>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div className="discovery-screenshot-state">
          <LuImages size={32} />
          <strong>No screenshots captured yet</strong>
          <span>Screenshots will appear here once the crawler captures pages of your application.</span>
        </div>
      );
    }

    return (
      <>
        <div className="discovery-screenshot-grid" role="list">
          {items.map((item, index) => (
            <ScreenshotCard
              key={item.key}
              screenshot={item.screenshot}
              index={index}
              onOpen={openPreview}
            />
          ))}
        </div>

        <div ref={loadMoreRef} className="discovery-screenshot-sentinel" aria-hidden="true" />

        <div className="discovery-screenshot-footer" aria-live="polite">
          {isFetchingNextPage ? (
            <span className="discovery-screenshot-footer-status">
              <LuLoader size={16} className="spinner" />
              Loading more screenshots...
            </span>
          ) : isFetchNextPageError ? (
            <span className="discovery-screenshot-footer-status is-error">
              Couldn&apos;t load more screenshots.
              <button type="button" className="discovery-screenshot-link-btn" onClick={loadMore}>
                <LuRefreshCw size={14} />
                Retry
              </button>
            </span>
          ) : hasNextPage ? (
            <span className="discovery-screenshot-footer-status">
              Showing {formatCount(items.length)} of {formatCount(displayTotal)} screenshots
              {supportsIntersectionObserver ? (
                <> &middot; scroll down to load more</>
              ) : (
                <button type="button" className="discovery-screenshot-link-btn" onClick={loadMore}>
                  Load more
                </button>
              )}
            </span>
          ) : (
            <span className="discovery-screenshot-footer-status">
              Showing all {formatCount(items.length)} screenshot{items.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </>
    );
  };

  return (
    <section className="discovery-screenshot-gallery" aria-labelledby={headingId}>
      <div className="discovery-screenshot-header">
        <h3 id={headingId} className="discovery-screenshot-heading">
          <LuImages size={18} />
          Crawled Screenshots
          {displayTotal > 0 && (
            <span className="discovery-screenshot-count">{formatCount(displayTotal)}</span>
          )}
        </h3>
        <p className="discovery-screenshot-subtitle">
          Pages captured while crawling your application. Click any screenshot to preview it in full screen.
        </p>
      </div>

      <div className="discovery-screenshot-panel">
        {renderPanelContent()}
      </div>

      {previewIndex !== null && items[previewIndex] && (
        <ScreenshotPreviewModal
          items={items}
          index={previewIndex}
          onClose={closePreview}
          onNavigate={navigatePreview}
          canLoadMore={Boolean(hasNextPage)}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={loadMore}
        />
      )}
    </section>
  );
};

export default DiscoveryScreenshotGallery;
